import { getCoverThumbUrl } from '../utils/coverUrl';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Star, Users, Infinity, Headphones, Play, CheckCircle, ArrowRight, Home, TrendingUp, Heart, Quote, Clock, X, Eye, Trash2, Menu } from 'lucide-react';
import { Button } from '../components/ui/button';
import TrendingSection from '../components/TrendingSection';
import UserAvatar from '../components/avatars/UserAvatar';
import BookwormCard from '../components/avatars/BookwormCard';
import FAQSection from '../components/landing/FAQSection';
import SiteFooter from '../components/landing/SiteFooter';
import axios from 'axios';

// Use current origin for deployed environment, fallback to env var for development
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// Helper to get the base URL for assets (images, files)
const getAssetBaseUrl = () => {
  return window.location.origin;
};

// Defensive helper: dedupe an array of books by their id field
// (or `book_id`) to avoid React "two children with the same key" warnings
// when the backend / cache layer occasionally returns the same book twice.
const dedupeById = (arr, idKey = 'id') => {
  if (!Array.isArray(arr)) return [];
  const seen = new Set();
  return arr.filter((it) => {
    const id = it?.[idKey];
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

const LandingPage = () => {
  const navigate = useNavigate();
  const [recentBooks, setRecentBooks] = useState([]);
  const [aiCollection, setAiCollection] = useState([]);
  const [recentReviews, setRecentReviews] = useState([]);
  const userRole = typeof window !== 'undefined' ? localStorage.getItem('userRole') : null;
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  const handleDeleteRecentReview = async (reviewId) => {
    if (!window.confirm('Delete this review? This will remove it from the landing page AND the book details page.')) return;
    try {
      await axios.delete(`${API}/reviews/${reviewId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRecentReviews((prev) => prev.filter((r) => r.id !== reviewId));
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to delete review');
    }
  };
  const [featuredTrailers, setFeaturedTrailers] = useState([]);
  const [showTrailerModal, setShowTrailerModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeTrailer, setActiveTrailer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [membership, setMembership] = useState({
    price: 4.99,
    currency: 'USD',
    plan_name: 'Monthly Subscription',
    badge_text: 'MOST POPULAR'
  });

  useEffect(() => {
    fetchRecentBooks();
    fetchAiCollection();
    fetchMembershipSettings();
    fetchRecentReviews();
    fetchFeaturedTrailers();

    // Refresh AI collection every 5 minutes (reduced from 30s for performance)
    const aiPollInterval = setInterval(fetchAiCollection, 300000);

    // Listen for `scb:home-reset` (fired by the navbar Home button click).
    // This refreshes recent data + closes the mobile menu so clicking Home
    // genuinely returns the page to a clean default state, even when the
    // user is already on `/`.
    const handleHomeReset = () => {
      setMobileMenuOpen(false);
      setShowTrailerModal(false);
      setActiveTrailer(null);
      fetchRecentBooks();
      fetchAiCollection();
      fetchRecentReviews();
      fetchFeaturedTrailers();
    };
    window.addEventListener('scb:home-reset', handleHomeReset);

    return () => {
      clearInterval(aiPollInterval);
      window.removeEventListener('scb:home-reset', handleHomeReset);
    };
  }, []);

  const fetchAiCollection = async () => {
    try {
      const response = await axios.get(`${API}/ai-collection?limit=6`);
      setAiCollection((response.data || []).slice(0, 6));
    } catch (error) {
      console.error('Error fetching AI collection:', error);
    }
  };

  const fetchMembershipSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/membership`);
      setMembership({
        price: response.data.price || 4.99,
        currency: response.data.currency || 'USD',
        plan_name: response.data.plan_name || 'Monthly Subscription',
        badge_text: response.data.badge_text || 'MOST POPULAR'
      });
    } catch (error) {
      console.error('Error fetching membership settings:', error);
    }
  };

  const fetchRecentBooks = async () => {
    try {
      const response = await axios.get(`${API}/books`);
      const sorted = response.data
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 6);
      setRecentBooks(sorted);
    } catch (error) {
      console.error('Error fetching books:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews/recent?limit=6`);
      setRecentReviews(response.data || []);
    } catch (error) {
      console.error('Error fetching recent reviews:', error);
    }
  };

  const fetchFeaturedTrailers = async () => {
    try {
      // Fetch all 3 featured trailers from the new endpoint
      const response = await axios.get(`${API}/featured-trailers`);
      
      if (response.data && response.data.length > 0) {
        setFeaturedTrailers(response.data);
        return;
      }
      
      // Fallback: Fetch all books with trailers (up to 3) if no featured set
      const fallbackResponse = await axios.get(`${API}/books-with-trailers`);
      if (fallbackResponse.data && fallbackResponse.data.length > 0) {
        const trailersWithDetails = await Promise.all(
          fallbackResponse.data.slice(0, 3).map(async (book, index) => {
            const positions = ['left', 'middle', 'right'];
            try {
              const bookResponse = await axios.get(`${API}/books/${book.id}`);
              return { ...bookResponse.data, slot: positions[index] };
            } catch (e) {
              return { ...book, slot: positions[index] };
            }
          })
        );
        setFeaturedTrailers(trailersWithDetails.filter(t => t && (t.trailer_url || t.trailer_external_url)));
      }
    } catch (error) {
      console.error('Error fetching featured trailers:', error);
    }
  };

  const openTrailerModal = (trailer) => {
    setActiveTrailer(trailer);
    setShowTrailerModal(true);
  };

  const getCoverUrl = (url) => {
    if (!url) return 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=400&q=80';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/api')) {
      return `${getAssetBaseUrl()}${url}`;
    }
    return url;
  };

  const getFileUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/api')) {
      return `${getAssetBaseUrl()}${url}`;
    }
    return url;
  };

  const features = [
    { icon: <Infinity className="h-8 w-8" />, title: 'Unlimited Access', description: 'Read as many books as you want with no limits or restrictions' },
    { icon: <Headphones className="h-8 w-8" />, title: 'Audiobooks Included', description: 'Listen to audiobooks while commuting, exercising, or relaxing' },
    { icon: <BookOpen className="h-8 w-8" />, title: 'Growing Library', description: 'New titles added every week across all genres and categories' },
    { icon: <TrendingUp className="h-8 w-8" />, title: 'Track Progress', description: 'Monitor your reading habits and set personal goals' }
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-gradient-to-b from-black via-black/90 to-transparent">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-24">
            <div className="flex items-center space-x-1 sm:space-x-3">
              <img
                src={`${process.env.REACT_APP_BACKEND_URL || ''}/api/static/scb-logo.png`}
                alt="Seven Chapter Books"
                className="h-16 sm:h-20 w-auto cursor-pointer mr-2 sm:mr-4 logo-hd"
                style={{ imageRendering: 'auto' }}
                loading="eager"
                decoding="sync"
                fetchPriority="high"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              />
              <Button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/10 hidden sm:inline-flex"
                data-testid="landing-nav-home"
              >
                <Home className="h-4 w-4 mr-1.5" />
                Home
              </Button>
              <Button
                onClick={() => navigate('/about')}
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/10 hidden sm:inline-flex"
                data-testid="landing-nav-about"
              >
                About
              </Button>
              <Button
                onClick={() => navigate('/contact')}
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/10 hidden sm:inline-flex"
                data-testid="landing-nav-contact"
              >
                Contact
              </Button>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-3">
              <Button
                onClick={() => navigate('/login')}
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/10"
                data-testid="landing-nav-signin"
              >
                Sign In
              </Button>
              <Button
                onClick={() => navigate('/signup')}
                size="sm"
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="landing-nav-signup"
              >
                Sign Up
              </Button>

              {/* Mobile hamburger — only visible below sm breakpoint */}
              <button
                type="button"
                onClick={() => setMobileMenuOpen((v) => !v)}
                className="sm:hidden p-2 rounded-md text-white hover:bg-white/10 transition-colors"
                aria-label="Open menu"
                aria-expanded={mobileMenuOpen}
                data-testid="landing-nav-mobile-toggle"
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile slide-down menu — only renders when open AND on small screens */}
        {mobileMenuOpen && (
          <div
            className="sm:hidden bg-black/95 backdrop-blur border-t border-white/10"
            data-testid="landing-nav-mobile-panel"
          >
            <div className="px-4 py-3 flex flex-col gap-1">
              {[
                { label: 'Home', testid: 'mobile-nav-home', onClick: () => { window.scrollTo({ top: 0, behavior: 'smooth' }); setMobileMenuOpen(false); } },
                { label: 'About', testid: 'mobile-nav-about', onClick: () => { navigate('/about'); setMobileMenuOpen(false); } },
                { label: 'Contact', testid: 'mobile-nav-contact', onClick: () => { navigate('/contact'); setMobileMenuOpen(false); } },
                { label: 'Sign In', testid: 'mobile-nav-signin', onClick: () => { navigate('/login'); setMobileMenuOpen(false); } },
              ].map((item) => (
                <button
                  key={item.label}
                  type="button"
                  onClick={item.onClick}
                  className="w-full text-left px-3 py-2.5 rounded-md text-white hover:bg-white/10 transition-colors text-sm font-medium"
                  data-testid={item.testid}
                >
                  {item.label}
                </button>
              ))}
              <button
                type="button"
                onClick={() => { navigate('/signup'); setMobileMenuOpen(false); }}
                className="w-full text-left px-3 py-2.5 rounded-md bg-red-600 hover:bg-red-700 text-white text-sm font-semibold mt-1"
                data-testid="mobile-nav-signup"
              >
                Sign Up
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-black">
          <div className="absolute top-1/4 -left-20 w-96 h-96 bg-red-600/30 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '4s' }} />
          <div className="absolute top-1/2 right-0 w-80 h-80 bg-purple-600/20 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '6s', animationDelay: '1s' }} />
          <div className="absolute bottom-1/4 left-1/3 w-72 h-72 bg-blue-600/20 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '5s', animationDelay: '2s' }} />
          
          {/* Grid Pattern Overlay */}
          <div className="absolute inset-0 opacity-5" style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                             linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }} />
          
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 text-center max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-8">
            <span className="animate-pulse h-2 w-2 bg-green-500 rounded-full"></span>
            <span className="text-sm text-gray-300">New titles added weekly</span>
          </div>
          
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold mb-6 leading-tight">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-white via-red-200 to-white">
              Unlimited Books.
            </span>
            <br />
            <span className="text-red-500">Exclusive Access.</span>
          </h1>
          
          <p className="text-lg sm:text-xl text-gray-300 mb-10 max-w-2xl mx-auto leading-relaxed">
            Dive into thousands of books, audiobooks, and exclusive content. 
            Join our private community of passionate readers.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate('/signup')}
              size="lg"
              className="bg-red-600 hover:bg-red-700 text-white text-lg px-8 py-6 shadow-lg shadow-red-600/30"
              data-testid="landing-hero-signup"
            >
              Start 7-Day Free Trial
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button
              onClick={() => navigate('/login')}
              size="lg"
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10 text-lg px-8 py-6"
              data-testid="landing-hero-signin"
            >
              Member Login
            </Button>
          </div>
        </div>
      </section>

      {/* Trending Now Section */}
      <TrendingSection />

      {/* Featured Book Trailers Section - Shows admin-selected featured trailer with position */}
      {featuredTrailers.length > 0 && (
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-black to-gray-900">
          <div className="max-w-screen-xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center space-x-2 bg-red-600/20 rounded-full px-4 py-2 mb-4">
                <Play className="h-4 w-4 text-red-500" />
                <span className="text-sm text-red-400 font-medium">Book Trailers</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                Preview Our Featured Books
              </h2>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                Watch trailers and get a glimpse of what awaits you inside our exclusive library
              </p>
            </div>

            {/* 3 Trailer Grid - Left, Middle, Right */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 max-w-7xl mx-auto">
              {featuredTrailers.map((trailer, idx) => {
                if (!trailer) return null;
                return (
                  <div 
                    key={trailer.book_id || idx}
                    className="bg-white/5 rounded-2xl overflow-hidden border border-white/10 hover:border-red-500/50 transition-all duration-300 group"
                    data-testid={`featured-trailer-${idx}`}
                  >
                    {/* Trailer Cover with Play Button */}
                    <div className="relative aspect-[2/3] overflow-hidden">
                      <img
                        src={getCoverThumbUrl(trailer.cover_image_url)}
                        alt={trailer.title}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        onError={(e) => {
                          e.target.src = 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=400&q=80';
                        }}
                      />
                      {/* Gradient Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
                      
                      {/* Play Button Overlay */}
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <button
                          onClick={() => openTrailerModal(trailer)}
                          className="bg-red-600 hover:bg-red-700 rounded-full p-5 transform scale-75 group-hover:scale-100 transition-all duration-300 shadow-xl shadow-red-600/30"
                        >
                          <Play className="h-10 w-10 text-white fill-white" />
                        </button>
                      </div>

                      {/* Trailer Badge */}
                      <div className="absolute top-3 left-3 bg-red-600 text-white text-xs px-2 py-1 rounded-full font-semibold flex items-center">
                        <Play className="h-3 w-3 mr-1 fill-white" />
                        TRAILER
                      </div>
                    </div>

                    {/* Book Info */}
                    <div className="p-5">
                      <h3 className="text-lg font-bold text-white mb-1 line-clamp-1">
                        {trailer.title}
                      </h3>
                      <p className="text-red-400 text-sm mb-3">by {trailer.author}</p>
                      <p className="text-gray-400 text-sm line-clamp-2 mb-4">
                        {trailer.description?.substring(0, 100)}...
                      </p>
                      <Button
                        onClick={() => openTrailerModal(trailer)}
                        className="w-full bg-red-600 hover:bg-red-700"
                      >
                        <Play className="h-4 w-4 mr-2 fill-white" />
                        Watch Trailer
                      </Button>
                    </div>
                  </div>
                );
              }).filter(Boolean)}
            </div>

            {/* CTA */}
            <div className="text-center mt-12">
              <p className="text-gray-500 text-sm mb-4">Join now to access all books and exclusive content</p>
              <Button
                onClick={() => navigate('/contact')}
                size="lg"
                variant="outline"
                className="border-red-500/50 text-white hover:bg-red-500/10"
              >
                Request Access
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Trailer Modal */}
      {showTrailerModal && activeTrailer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4">
          <div className="relative w-full max-w-4xl">
            <button
              onClick={() => {
                setShowTrailerModal(false);
                setActiveTrailer(null);
              }}
              className="absolute -top-12 right-0 text-white hover:text-red-500 transition-colors"
            >
              <X className="h-8 w-8" />
            </button>
            <div className="aspect-video bg-black rounded-xl overflow-hidden">
              {activeTrailer.trailer_external_url ? (() => {
                const url = activeTrailer.trailer_external_url;
                const ytMatch = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/))([a-zA-Z0-9_-]{11})/);
                if (ytMatch) {
                  return (
                    <iframe
                      src={`https://www.youtube.com/embed/${ytMatch[1]}?autoplay=1`}
                      title="Book Trailer"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full h-full"
                      style={{ border: 'none' }}
                    />
                  );
                }
                if (url.includes('facebook.com') || url.includes('fb.watch')) {
                  return (
                    <iframe
                      src={`https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&show_text=false&width=740&autoplay=true`}
                      className="w-full h-full"
                      style={{ border: 'none', overflow: 'hidden' }}
                      scrolling="no"
                      frameBorder="0"
                      allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                      allowFullScreen
                      title="Book Trailer"
                    />
                  );
                }
                return <a href={url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center h-full text-white">Watch Trailer</a>;
              })() : (
                <video
                  src={getFileUrl(activeTrailer.trailer_url)}
                  controls
                  autoPlay
                  playsInline
                  preload="auto"
                  className="w-full h-full"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = '<div class="flex items-center justify-center h-full text-gray-400"><p>Trailer unavailable. Please try again later.</p></div>';
                  }}
                >
                  Your browser does not support the video tag.
                </video>
              )}
            </div>
            <div className="mt-4 text-center">
              <h3 className="text-xl font-bold text-white">{activeTrailer.title}</h3>
              <p className="text-gray-400">by {activeTrailer.author}</p>
            </div>
          </div>
        </div>
      )}

      {/* Our Library Section - AI Powered Collection */}
      {aiCollection.length > 0 && (
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-screen-xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 bg-green-500/20 rounded-full px-4 py-2 mb-4">
              <BookOpen className="h-4 w-4 text-green-500" />
              <span className="text-sm text-green-400 font-medium">AI-Powered Selection</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Our Original Collection
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Curated by our AI based on what's featured, popular, trending, and new.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 sm:gap-6">
            {dedupeById(aiCollection).map((book) => (
              <div 
                key={book.id}
                data-testid={`ai-collection-book-${book.id}`}
                className="group relative rounded-lg overflow-hidden transition-all duration-300 hover:scale-105 hover:z-10"
              >
                <img
                  src={book.cover?.startsWith('http') ? book.cover : `${window.location.origin}${book.cover}`}
                  alt={book.title}
                  loading="lazy"
                  className="w-full aspect-[2/3] object-cover"
                  onError={(e) => {
                    e.target.src = 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=400&q=80';
                  }}
                />
                {/* Always-visible view count badge */}
                <div className="absolute bottom-2 right-2 bg-black/70 backdrop-blur-sm rounded-full px-2 py-0.5 flex items-center gap-1 z-10">
                  <Eye className="h-3 w-3 text-blue-400" />
                  <span className="text-[10px] text-white font-medium">{book.total_views || 0}</span>
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    <h3 className="text-sm font-semibold text-white truncate">{book.title}</h3>
                    <p className="text-xs text-gray-300 truncate">by {book.author}</p>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center">
                        <Star className="h-3 w-3 text-yellow-500 fill-yellow-500 mr-1" />
                        <span className="text-xs text-yellow-500">{book.rating?.toFixed(1) || '4.5'}</span>
                      </div>
                      <div className="flex items-center text-xs text-gray-400">
                        <Eye className="h-3 w-3 mr-1" />
                        {book.total_views || 0}
                      </div>
                    </div>
                  </div>
                </div>
                <div className={`absolute top-2 left-2 ${book.badgeColor || 'bg-blue-600'} text-white text-[10px] px-2 py-1 rounded font-bold shadow-lg`}>
                  {book.badge || 'STAFF PICK'}
                </div>
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Heart className="h-5 w-5 text-white hover:text-red-500 hover:fill-red-500 transition-colors" />
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <p className="text-gray-500 text-sm mb-4">Discover exclusive titles from our featured authors</p>
            <Button
              onClick={() => navigate('/contact')}
              size="lg"
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <BookOpen className="mr-2 h-5 w-5" />
              Browse Collection
            </Button>
          </div>
        </div>
      </section>
      )}

      {/* Recently Added Books Section */}
      {recentBooks.length > 0 && (
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-black to-gray-900">
          <div className="max-w-screen-xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                Recently Added
              </h2>
              <p className="text-gray-400 text-lg">
                Fresh titles waiting for you. New books added every week.
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 sm:gap-6">
              {recentBooks.map((book) => (
                <div 
                  key={book.id} 
                  className="group relative rounded-lg overflow-hidden transition-transform duration-300 hover:scale-105 hover:z-10"
                >
                  <img
                    src={getCoverThumbUrl(book.cover_image_url)}
                    alt={book.title}
                    loading="lazy"
                    className="w-full aspect-[2/3] object-cover"
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=400&q=80';
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-0 left-0 right-0 p-3">
                      <h3 className="text-sm font-semibold text-white truncate">{book.title}</h3>
                      <p className="text-xs text-gray-300 truncate">{book.author}</p>
                    </div>
                  </div>
                  <div className="absolute top-2 left-2 bg-red-600 text-white text-xs px-2 py-1 rounded font-semibold">
                    NEW
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-10">
              <Button
                onClick={() => navigate('/contact')}
                size="lg"
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Browse All Books
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Why Choose Us Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-900">
        <div className="max-w-screen-xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Why Readers Love Us
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Join thousands of book lovers who have transformed their reading habits 
              with unlimited access to our growing library.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:border-red-500/50 transition-colors"
              >
                <div className="text-red-500 mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-black">
        <div className="max-w-screen-xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Start Reading in 3 Simple Steps
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                1
              </div>
              <h3 className="text-xl font-semibold mb-2">Create Account</h3>
              <p className="text-gray-400">Sign up in seconds with just your email. No credit card required to browse.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                2
              </div>
              <h3 className="text-xl font-semibold mb-2">Subscribe</h3>
              <p className="text-gray-400">Choose our affordable ${membership.price.toFixed(2)}/month plan. Cancel anytime, no hidden fees.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                3
              </div>
              <h3 className="text-xl font-semibold mb-2">Read Unlimited</h3>
              <p className="text-gray-400">Access our entire library instantly. Read or listen to any book, anytime.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Recently Reviewed Section - Replaces Testimonials */}
      {recentReviews.length > 0 && (
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-900 to-black">
          <div className="max-w-screen-xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-flex items-center space-x-2 bg-purple-500/20 rounded-full px-4 py-2 mb-4">
                <Quote className="h-4 w-4 text-purple-400" />
                <span className="text-sm text-purple-400 font-medium">Community Reviews</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                Recently Reviewed by Members
              </h2>
              <p className="text-gray-400 text-lg">
                See what our community is reading and recommending
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {recentReviews.map((review, index) => (
                <div 
                  key={review.id || index}
                  className="relative bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:border-purple-500/30 transition-colors"
                  data-testid="recent-review-card"
                >
                  {userRole === 'admin' && review.id && (
                    <button
                      onClick={() => handleDeleteRecentReview(review.id)}
                      className="absolute top-2 right-2 text-gray-500 hover:text-red-400 p-1.5 rounded-full bg-black/40 hover:bg-black/60 transition-colors z-10"
                      title="Delete review (admin)"
                      data-testid={`delete-recent-review-${review.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                  <div className="flex items-start space-x-4">
                    {/* Book Cover Thumbnail */}
                    <img
                      src={getCoverThumbUrl(review.book_cover)}
                      alt={review.book_title}
                      loading="lazy"
                      className="w-16 h-24 object-cover rounded-lg flex-shrink-0"
                      onError={(e) => {
                        e.target.src = 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=400&q=80';
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="text-white font-semibold truncate">{review.book_title}</h4>
                      <p className="text-gray-400 text-sm truncate">by {review.book_author}</p>
                      
                      {/* Star Rating */}
                      <div className="flex items-center mt-2 mb-3">
                        {[...Array(5)].map((_, i) => (
                          <Star 
                            key={i} 
                            className={`h-4 w-4 ${i < review.rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-600'}`} 
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  {/* Review Comment */}
                  <p className="text-gray-300 text-sm mt-4 line-clamp-3 italic">
                    "{review.comment}"
                  </p>
                  
                  {/* Reviewer Info */}
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
                    <BookwormCard
                      userId={review.user_id}
                      name={review.user_name}
                      avatarId={review.user_avatar_id}
                      avatarPhotoUrl={review.user_avatar_photo_url}
                    >
                      <span className="inline-flex items-center gap-2 min-w-0">
                        <UserAvatar
                          name={review.user_name}
                          avatarId={review.user_avatar_id}
                          avatarPhotoUrl={review.user_avatar_photo_url}
                          size="sm"
                          testId={`recent-review-avatar-${review.id || index}`}
                        />
                        <span className="text-gray-400 text-sm truncate">{review.user_name}</span>
                      </span>
                    </BookwormCard>
                    <div className="flex items-center text-gray-500 text-xs">
                      <Clock className="h-3 w-3 mr-1" />
                      {new Date(review.timestamp).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-10">
              <p className="text-gray-500 text-sm mb-4">Join our community and share your own reviews</p>
              <Button
                onClick={() => navigate('/contact')}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                Become a Member
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Final CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-red-900 to-red-700 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-0 w-full h-full" style={{
            backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px)',
            backgroundSize: '30px 30px'
          }} />
        </div>
        <div className="relative max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Ready to Start Your Reading Journey?
          </h2>
          <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
            Join our exclusive community of book lovers. Contact us to request access.
          </p>
          <Button
            onClick={() => navigate('/contact')}
            size="lg"
            className="bg-white text-red-700 hover:bg-gray-100 text-lg px-8 py-6"
          >
            Request Access
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Frequently Asked Questions (Netflix-style accordion) */}
      <FAQSection />

      {/* Site Footer (Netflix-style multi-column links) */}
      <SiteFooter />
    </div>
  );
};

export default LandingPage;
